import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Keeps one broken section (or page) from taking down the rest of the site. Sections get the
 * compact fallback; the page boundary offers a reload.
 */
type Props = { children: ReactNode; fallback?: ReactNode; label?: string; resetKey?: string };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) console.error(`[${this.props.label ?? "section"}]`, error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;
    return (
      <div className="container">
        <div className="state state--error" role="alert">
          <div className="state__body">
            <p className="state__title">This part of the page didn't load</p>
            <p className="state__text">Please refresh the page. If it keeps happening, let us know at ask@miracleassembly.org.</p>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => window.location.reload()}>
            Refresh
          </button>
        </div>
      </div>
    );
  }
}
